import CheckIcon from "@material-ui/icons/Check";
import EditIcon from "@material-ui/icons/Edit";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";

import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import Button from "../../../../../components/elements/Button";
import DomainList from "../DomainList";
import EditCustomDomainListModal from "../EditCustomDomainListModal";
import classes from "../../PublicationListSelectionSlide.module.scss";
import { CustomDomainList, Domain } from "../../../../../types/campaignTypes";
import { truncateText } from "../../../../../helpers/truncateText";

type CustomDomainListsDashboardProps = {
  customDomainLists: CustomDomainList[];
  selectedDomainListsIds: number[];
  toggleSelectDomainLists: (beeswaxListId: number) => void;
  updateDomainList: (
    bgAdvertiserId: string,
    beeswaxListId: number,
    domainListId: string,
    listName: string,
    domainsToUpdate: Domain[],
    domainsToAdd: string[],
    saveCreatedDomainList: (updatedDomainLists: CustomDomainList[]) => void,
  ) => void;
};

const CustomDomainListsDashboard: FC<CustomDomainListsDashboardProps> = ({
  customDomainLists,
  selectedDomainListsIds,
  toggleSelectDomainLists,
  updateDomainList,
}) => {
  const [customDomainListToEdit, setCustomDomainListToEdit] = useState<CustomDomainList | null>(null);

  const handleOpenModal = (dataToEdit: CustomDomainList) => () => setCustomDomainListToEdit(dataToEdit);
  const handleCloseModal = () => setCustomDomainListToEdit(null);

  const columns = [
    { key: "name", label: "Name", minWidth: 350 },
    { key: "actions", label: "Actions", minWidth: 100 },
  ] as const;

  const rows = customDomainLists
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .map(({ beeswaxListId, name, domains }) => {
      const isSelected = selectedDomainListsIds.includes(beeswaxListId);

      return {
        id: beeswaxListId.toString(),
        name: (() => (
          <Tooltip title={name}>
            <Typography>{truncateText(name, 80)}</Typography>
          </Tooltip>
        ))(),
        actions: (() => (
          <div className={classes.actions}>
            <Button
              className={classes.selectButton}
              variant={isSelected ? "contained" : "outlined"}
              onClick={() => toggleSelectDomainLists(beeswaxListId)}
            >
              {isSelected ? "Selected" : "Select"}
              <CheckIcon />
            </Button>

            <IconButton
              className={classes.editButton}
              onClick={handleOpenModal(
                customDomainLists.find(
                  (customDomainList) => customDomainList.beeswaxListId === beeswaxListId,
                ) as CustomDomainList,
              )}
            >
              <EditIcon />
            </IconButton>
          </div>
        ))(),
        collapsableConfigs: {
          collapsable: true,
          collapsableChild: <DomainList domainList={domains} />,
        },
      };
    });

  return (
    <div className={classes.domainListsDashboardContainer}>
      <EditCustomDomainListModal
        isOpen={Boolean(customDomainListToEdit)}
        handleClose={handleCloseModal}
        customDomainListToEdit={customDomainListToEdit}
        updateDomainList={updateDomainList}
      />

      <div className={classes.header}>
        <Typography variant="h2">Custom Publication Lists</Typography>

        {customDomainLists.length === 0 ? (
          <Typography>These are no custom domain lists yet!</Typography>
        ) : (
          <Typography>These are all of the lists that you've created previously</Typography>
        )}
      </div>

      <BaseDataTable columns={columns} rows={rows} />
    </div>
  );
};

export default CustomDomainListsDashboard;
